import { useMemo } from 'react';

import { BDTO } from '@helpful/network';
import { useContext } from 'use-context-selector';
import { useBootstrapData } from '@bootstrap/hooks';
import { useTranslation } from '@wearehelpful/ui-localization';
import { usePatient } from '@common/hooks';
import { useRoute } from '@core/navigation';
import { RoutePath } from '@common/constants';

import { BenefitsProviderContext } from './BenefitsProvider.context';
import {
  FetchNextPageFn,
  RefetchFn,
  Filter,
  CategoryOption,
  RouteSearch,
} from './BenefitsProvider.types';

interface UseBenefitsReturnState {
  data: BDTO.PlanBenefitItem[];
  hasNextPage: boolean;
  total: number;
  isLoading: boolean;
  filters: BDTO.GetPlanBenefitsParams;
  categories: CategoryOption[];
  meta?: BDTO.GetPlanBenefitsMeta;
}

interface UseBenefitsReturnDespatch {
  fetchNextPage: FetchNextPageFn;
  refetch: RefetchFn;
  filter: Filter;
}

type UseBenefitsReturnType = [
  UseBenefitsReturnState,
  UseBenefitsReturnDespatch
];

export const useBenefits = (): UseBenefitsReturnType => {
  const { t } = useTranslation();
  const { stacks } = useBootstrapData();

  const {
    data,
    hasNextPage,
    total,
    isLoading,
    fetchNextPage,
    refetch,
    filter,
    filters,
    meta,
  } = useContext(BenefitsProviderContext);

  const categories = useMemo((): CategoryOption[] => {
    return [
      {
        value: undefined,
        title: t('options.all'),
      },
    ].concat(
      stacks.map(({ title, id }) => ({
        title,
        value: id,
      }))
    );
  }, [stacks, t]);

  return [
    { data, hasNextPage, total, isLoading, filters, categories, meta },
    { fetchNextPage, refetch, filter },
  ];
};

export const useBenefitsProvider = () => {
  const [{ search, pathname }] = useRoute<{}, RouteSearch>();

  const [
    {
      plans: [plan],
    },
  ] = usePatient();

  const slug = (() => {
    if (pathname === RoutePath.ROOT_BENEFITS && search.plan) {
      return search.plan;
    }
    return plan;
  })();

  return { slug };
};
